import clsx from 'clsx'
import { useMemo, useState } from 'react'
import * as Select from '@radix-ui/react-select'
import { HugeiconsIcon, HugeiconsIconProps } from '@hugeicons/react'

import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  Tick01Icon
} from '@hugeicons/core-stroke-rounded'

export type ValueType = string | number

export interface Option<T extends ValueType> {
  label: string
  value: T
  group?: string
  Icon?: HugeiconsIconProps['icon']
}

interface Group {
  name: string
  options: Option<ValueType>[]
}

function isGroup(elem: Option<ValueType> | Group): elem is Group {
  return 'name' in elem
}

export interface DropdownProps<T extends ValueType> {
  options: Option<T>[]
  value?: T | null
  placeholder?: string
  name?: string
  invalid?: boolean
  disabled?: boolean
  readOnly?: boolean
  autoComplete?: string
  onChange: (values: T) => void
}

function OptionItem({ option }: { option: Option<ValueType> }) {
  return (
    <Select.Item
      key={option.value}
      value={option.value.toString()}
      className="group flex cursor-pointer flex-row items-center gap-2 rounded-small p-2 focus:bg-primary-bright focus:outline-hidden"
    >
      <Select.ItemIndicator>
        <HugeiconsIcon icon={Tick01Icon} className="size-5" />
      </Select.ItemIndicator>
      <div className="group-data-[state=checked]:hidden size-5" />
      {option.Icon && <HugeiconsIcon icon={option.Icon} className="size-5" />}
      <Select.ItemText>{option.label}</Select.ItemText>
    </Select.Item>
  )
}

function GroupItem({ group }: { group: Group }) {
  return (
    <Select.Group>
      <Select.Label className="text-text-light px-2">{group.name}</Select.Label>
      {group.options.map(option => (
        <OptionItem key={option.value} option={option} />
      ))}
    </Select.Group>
  )
}

export default function Dropdown<T extends ValueType>({
  options,
  value,
  placeholder,
  name,
  invalid = false,
  readOnly = false,
  disabled = false,
  autoComplete,
  onChange
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false)

  const groupedOptions = useMemo(() => {
    return options.reduce<(Group | Option<T>)[]>((acc, option) => {
      if (option.group) {
        const groupIndex = acc.findIndex(
          g => isGroup(g) && g.name === option.group
        )

        if (groupIndex < 0) acc.push({ name: option.group, options: [option] })
        else (acc[groupIndex] as Group).options.push(option)
      }

      if (!option.group) {
        acc.push(option)
      }

      return acc
    }, [])
  }, [options])

  return (
    <Select.Root
      value={value?.toString() ?? ''}
      onValueChange={value => onChange(value as T)}
      open={open}
      onOpenChange={setOpen}
      autoComplete={autoComplete}
      disabled={disabled || readOnly}
    >
      <Select.Trigger
        className={clsx(
          'w-full flex flex-row items-center gap-1 rounded-small bg-white text-left',
          invalid && 'border-red-500',
          !readOnly &&
            'border border-text select-none py-1.5 pl-2 pr-3 cursor-pointer',
          readOnly && 'border-none cursor-text p-0'
        )}
        aria-invalid={invalid}
        name={name}
      >
        {!readOnly && (
          <Select.Icon>
            <HugeiconsIcon icon={ArrowDown01Icon} className="size-4" />
          </Select.Icon>
        )}
        <Select.Value placeholder={placeholder} />
      </Select.Trigger>
      <Select.Content
        position="popper"
        collisionPadding={10}
        onPointerDownOutside={() => setOpen(false)}
        className="max-h-(--radix-select-content-available-height) data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 flex flex-col gap-1 rounded-small bg-white p-3 shadow-md outline-hidden overflow-y-auto z-popover"
      >
        <Select.ScrollUpButton>
          <HugeiconsIcon icon={ArrowUp01Icon} className="size-4 mx-auto" />
        </Select.ScrollUpButton>
        <Select.Viewport>
          {groupedOptions.map(function (option) {
            if (isGroup(option)) {
              return <GroupItem key={option.name} group={option} />
            }

            return <OptionItem key={option.value} option={option} />
          })}
        </Select.Viewport>
        <Select.ScrollDownButton>
          <HugeiconsIcon icon={ArrowDown01Icon} className="size-4 mx-auto" />
        </Select.ScrollDownButton>
      </Select.Content>
    </Select.Root>
  )
}
